import React, { useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@apollo/client';
import useDocumentVisibility from '@rehooks/document-visibility';
import classNames from 'classnames';
import sharedStyles from '../Home.module.scss';
import { IMPORT_ACTIVITY_QUERY } from './queries';
import ImportHistory from './components/ImportHistory';
import ImportInProgress from './components/ImportInProgress';
import styles from './ImportReferencesSummary.module.css';
import BatchInProgress from './components/BatchInProgress';
import accordionHeaderStyles from 'components/core/Accordion/components/AccordionHeader.module.scss';
import buttonStyles from 'components/core/Button/Button.module.scss';
import { captureApolloError } from 'util/apolloError';
import LoadingSpinner from 'system/elements/Spinner';
import ErrorMessage from 'components/ErrorMessage';
import { ImportActivityQuery, ImportFragment } from 'types/graphql';
import { ID, matchType } from 'util/localTypes';
import Panel from 'system/elements/Panel';
import Flex from 'system/base/Flex';
import { SummaryTitle } from 'system/base/Heading';
import Box from 'system/base/Box';
import { useGetLatestAutoExclusionBatchQuery } from 'query/review';
import { usePrevious } from 'hooks/usePrevious';
import pollingInterval from 'util/pollingInterval';
import { useReviewerFeatures } from 'hooks/reviewers/useReviewerFeatures';
import {
  AccordionContent,
  AccordionControls,
  AccordionGroup,
  AccordionHeader,
  Button,
  Link,
  Icon,
  IconList,
} from 'components/core';
import { Inline, Stack } from 'components/layout';
import { useAccordionItem } from 'components/core/Accordion/AccordionItemContext';

const LoadingView = () => (
  <Panel marginBottom={4}>
    <Flex justifyContent="space-between" alignItems="center">
      <SummaryTitle unpadded fontWeight="lighter" color="greyDarker">
        Import references
      </SummaryTitle>
      <LoadingSpinner />
    </Flex>
  </Panel>
);

type ImportReferencesHeaderControlsProps = {
  autoExcludeCount: number;
  checkDuplicatesLink: (importId: string | null) => string;
  importInProgress: boolean;
  isAutoExclusionSupported: boolean;
  newImportLink: string;
  reviewId: ID;
  totalDuplicates?: number | null;
};

const autoExclusionsListLink = (reviewId: string) => {
  return `/reviews/${reviewId}/auto_exclusions`;
};

const renderImportReferencesHeaderControls = ({
  autoExcludeCount,
  checkDuplicatesLink,
  importInProgress,
  isAutoExclusionSupported,
  newImportLink,
  reviewId,
  totalDuplicates,
}: ImportReferencesHeaderControlsProps) => (
  <AccordionControls>
    <Inline
      spacing={4}
      alignX="right"
      className={sharedStyles.ImportControlsContainer}
    >
      <Stack className={sharedStyles.ImportControlsLinkStack}>
        <Link
          to={checkDuplicatesLink(null)}
          variant="primary"
          className={sharedStyles.ControlsLink}
        >
          {totalDuplicates ?? 0} total duplicates removed
        </Link>
        {isAutoExclusionSupported && (
          <Link
            to={autoExclusionsListLink(reviewId)}
            variant="primary"
            className={sharedStyles.ControlsLink}
          >
            {autoExcludeCount} auto-marked as ineligible
          </Link>
        )}
      </Stack>
      {importInProgress ? (
        <Box>
          <div className={styles.orb} /> Import in progress
        </Box>
      ) : (
        <Link
          to={newImportLink}
          component={Button}
          type="brand"
          className={sharedStyles.PrimaryActionButton}
        >
          <Icon icon={IconList.solid.faCloudUploadAlt} /> Import
        </Link>
      )}
    </Inline>
  </AccordionControls>
);

const ImportReferencesStaticHeader = ({
  autoExcludeCount,
  checkDuplicatesLink,
  importInProgress,
  isAutoExclusionSupported,
  newImportLink,
  reviewId,
  totalDuplicates,
}: ImportReferencesHeaderControlsProps) => (
  <div
    className={classNames(
      accordionHeaderStyles.AccordionHeader,
      sharedStyles.AccordionHeader
    )}
  >
    <div role="heading" aria-level={3}>
      <div
        className={classNames(
          buttonStyles.Button,
          accordionHeaderStyles.AccordionHeader_button,
          styles.StaticAccordionHeaderContent
        )}
      >
        <span
          className={classNames([
            sharedStyles.AccordionHeaderTitle,
            sharedStyles.ImportHeaderTitle,
          ])}
        >
          Import references
        </span>
      </div>
    </div>
    <div
      className={classNames(
        accordionHeaderStyles.AccordionControlsContainer,
        styles.StaticAccordionControlsContainer
      )}
    >
      {renderImportReferencesHeaderControls({
        autoExcludeCount,
        checkDuplicatesLink,
        importInProgress,
        isAutoExclusionSupported,
        newImportLink,
        reviewId,
        totalDuplicates,
      })}
    </div>
  </div>
);

export type ImportReferencesSummaryProps = {
  reviewId: ID;
  isOpen: boolean;
  newImportLink: string;
  historyLink: string;
  isAutoExclusionEnabled: boolean;
  isAutoExclusionSupported: boolean;
  onImportStart?: (i: ImportFragment) => void;
  onImportFinish?: (i: ImportFragment) => void;
};

const isInProgress = (i: ImportFragment | null | undefined): boolean => {
  if (!i) return false;
  return !i.isFinished;
};

export const pollInterval = pollingInterval(6000);

const ImportReferencesSummaryWrapper = (
  props: ImportReferencesSummaryProps
) => {
  return (
    <AccordionGroup
      className={sharedStyles.AccordionGroup}
      initiallyOpen={props.isOpen}
    >
      <ImportReferencesSummary {...props} />
    </AccordionGroup>
  );
};

const ImportReferencesSummary = ({
  reviewId,
  newImportLink,
  historyLink,
  isAutoExclusionEnabled,
  isAutoExclusionSupported,
  onImportStart = () => null,
  onImportFinish = () => {
    window.location.reload();
  },
}: ImportReferencesSummaryProps) => {
  const { isOpen, setIsOpen } = useAccordionItem();

  const documentVisibility = useDocumentVisibility();
  const polling = documentVisibility !== 'hidden' && isOpen;

  useReviewerFeatures();

  const {
    data: importData,
    error: importError,
    loading: importLoading,
  } = useQuery<ImportActivityQuery>(IMPORT_ACTIVITY_QUERY, {
    pollInterval: polling ? pollInterval : 0,
    variables: { reviewId },
  });

  const fullPageRefresh = () => {
    window.location.reload();
  };

  const currentImport = useMemo(() => {
    const review = matchType(importData?.review, 'Review');
    return !importError && !importLoading ? review?.currentImport : undefined;
  }, [importData, importError, importLoading]);
  const importInProgress = isInProgress(currentImport);
  const hasImports = Boolean(currentImport);
  const oldImportRef = useRef<ImportFragment>();

  const enableBatching = () => {
    // Dont run if feature disabled
    if (!isAutoExclusionEnabled) return false;

    // Dont run if loading imports, review has no imports or there is an error
    return !importLoading && !importInProgress && hasImports && !importError;
  };
  const { data: batchData } = useGetLatestAutoExclusionBatchQuery(reviewId, {
    enabled: enableBatching(),
  });
  const autoExcludeCount = batchData?.review_total_auto_exclusions_count || 0;
  const isBatching = batchData?.isBatching;
  const wasBatching = usePrevious(isBatching);

  useEffect(() => {
    const oldCurrentImport = oldImportRef.current;
    const importWasInProgress = isInProgress(oldCurrentImport);
    const importIsInProgress = isInProgress(currentImport);

    const importChanged =
      importWasInProgress !== importIsInProgress ||
      (importIsInProgress &&
        importWasInProgress &&
        oldCurrentImport!.id !== currentImport!.id);

    if (importChanged) {
      if (importWasInProgress) {
        onImportFinish(oldCurrentImport as ImportFragment);
      }

      if (importIsInProgress) {
        onImportStart(currentImport as ImportFragment);
      }
    }

    oldImportRef.current = currentImport as ImportFragment;
  }, [currentImport, onImportFinish, onImportStart]);

  // Open panel if import is in progress
  useEffect(() => {
    if (importInProgress) {
      setIsOpen(true);
    }
  }, [importInProgress]);

  // Open panel if batch is in progress
  useEffect(() => {
    if (isBatching) {
      setIsOpen(true);
    }
  }, [isBatching]);

  // refresh page if batch and import are finished
  useEffect(() => {
    if (wasBatching && !isBatching && !importInProgress) {
      fullPageRefresh();
    }
  }, [isBatching, importInProgress, wasBatching]);

  if ((importLoading || !importData) && !importError) {
    return <LoadingView />;
  }

  if (importError) {
    if (importError) captureApolloError(importError);
    // Ticket to improve this state https://covidence.atlassian.net/browse/COV-2120
    return <ErrorMessage>Something went wrong, try again later.</ErrorMessage>;
  }

  const importReview = matchType(importData?.review, 'Review');
  const recentImports = importReview?.imports.nodes;
  const totalDuplicates = importReview?.duplicates.totalDuplicates;

  const checkDuplicatesLink = (importId: string | null) => {
    if (importId) {
      return `/reviews/${reviewId}/duplicates?import_id=${importId}`;
    }

    return `/reviews/${reviewId}/duplicates`;
  };

  const hasImportHistory =
    Boolean(currentImport) || Boolean(recentImports?.length);

  if (!hasImportHistory) {
    return (
      <ImportReferencesStaticHeader
        autoExcludeCount={autoExcludeCount}
        checkDuplicatesLink={checkDuplicatesLink}
        importInProgress={importInProgress}
        isAutoExclusionSupported={isAutoExclusionSupported}
        newImportLink={newImportLink}
        reviewId={reviewId}
        totalDuplicates={totalDuplicates}
      />
    );
  }

  return (
    <>
      <AccordionHeader
        className={sharedStyles.AccordionHeader}
        chevronPosition="right"
        level={3}
      >
        <span
          className={classNames([
            sharedStyles.AccordionHeaderTitle,
            sharedStyles.ImportHeaderTitle,
          ])}
        >
          Import references
        </span>
        {renderImportReferencesHeaderControls({
          autoExcludeCount,
          checkDuplicatesLink,
          importInProgress,
          isAutoExclusionSupported,
          newImportLink,
          reviewId,
          totalDuplicates,
        })}
      </AccordionHeader>
      <AccordionContent className={styles.SummaryAccordion}>
        <Stack
          className={styles.SummaryAccordionContent}
          data-testid="importReferencesPanelContent"
        >
          {importInProgress && (
            <ImportInProgress currentImport={currentImport} />
          )}
          {!importInProgress &&
            isAutoExclusionSupported &&
            isAutoExclusionEnabled && (
              <BatchInProgress batch={batchData?.latest_batch} />
            )}

          <ImportHistory
            checkDuplicatesLink={checkDuplicatesLink}
            recentImports={recentImports as readonly ImportFragment[]}
            viewMore={historyLink}
          />
        </Stack>
      </AccordionContent>
    </>
  );
};

export default ImportReferencesSummaryWrapper;
